import { useCallback } from 'react';

import { getParsedGraphQLFirstError } from 'src/lib/graphql';
import { useFreshRef } from 'src/lib/hooks';
import { useNotificationActions } from 'src/store';

import { useErrorHandler } from './useErrorHandler';

/**
 * Handle GraphQL errors and display notifications based on the error code.
 * Falls back to the generic error handler if no custom message is found.
 *
 * @param {function} getErrorMessage - Function that returns a custom message based on the error code
 * @returns {(function(Error, Object|null): void)} - Error handler function
 */
// TODO: consider merging this with useErrorHandler
export const useGraphQLErrorHandler = ({ getErrorMessage }) => {
  const notifications = useNotificationActions();
  const handleError = useErrorHandler();

  const refGetErrorMessage = useFreshRef(getErrorMessage);

  return useCallback(
    (error, extra) => {
      if (refGetErrorMessage.current) {
        if (error?.graphQLErrors) {
          const parsedError = getParsedGraphQLFirstError(error);
          if (parsedError) {
            const errorMessage = refGetErrorMessage.current?.(parsedError.code);
            if (errorMessage) {
              notifications.displayError({ message: errorMessage });
              return;
            }
          }
        }

        if (error?.code) {
          const errorMessage = refGetErrorMessage.current?.(error.code);
          if (errorMessage) {
            notifications.displayError({ message: errorMessage });
            return;
          }
        }
      }

      handleError(error, extra);
    },
    [handleError, refGetErrorMessage, notifications]
  );
};
